import { Menu, MenuItem, Stack } from "@mui/material";
import { useState } from "react";
import ReactEcharts from "echarts-for-react";
import { ConfigureParametersChartDialog } from "../../portfolio-info/components/ConfigureParametersChartDialog";
import {
    chartTooltipService
} from "../../portfolio-profile/components/charts/portfolio-greek-profile/services/ChartTooltipService";
import {
    usePortfolioSelection,
} from "../../../shared/contexts/portfolio-context/components/PortfolioContextProvider";
import { PortfolioBalanceHistoryEntryModel } from "../../portfolio-info/types/PortfolioBalanceHistoryEntryModel";
import { PortfolioSettingsModel } from "../../portfolio-info/types/PortfolioSettingsModel";
import { OrderModel } from "../../portfolio-info/types/OrderModel";

export function PortfolioBalanceHistory() {
    const {
        selectedPortfolio,
    } = usePortfolioSelection();

    const [isConfigureChartDialogOpen, setIsConfigureChartDialogOpen] = useState<boolean>(false);
    const [isChartTooltipVisible, setIsChartTooltipVisible] = useState<boolean>(true);

    const [contextMenu, setContextMenu] = useState<{
        mouseX: number;
        mouseY: number;
    } | null>(null);
    
    if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
        return null;
    }
    
    const settings = selectedPortfolio.portfolioSettings;

    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault();
        setContextMenu(
            contextMenu === null
                ? {
                    mouseX: event.clientX + 2,
                    mouseY: event.clientY - 6,
                }
                : null,
        );
    };

    const handleClose = (event: any) => {
        setContextMenu(null);
        event.stopPropagation();
    };

    const handleOpeningConfigureChartsDialog = (event: any) => {
        setContextMenu(null);
        event.stopPropagation();
        setIsConfigureChartDialogOpen(true);
    };
    
    function handleChangeChartTooltipVisibility() {
        setIsChartTooltipVisible(!isChartTooltipVisible);
        setContextMenu(null);
    }

    const data = selectedPortfolio.portfolioBalanceHistory
        .sort((x:  PortfolioBalanceHistoryEntryModel, y:  PortfolioBalanceHistoryEntryModel) =>
            new Date(x.createdAt).getTime() >
            new Date(y.createdAt).getTime()
                ? 1
                : -1)
    
    const dataSource: any[][] = getDataSource(
        data, selectedPortfolio.portfolioSettings);
    
    return (
        <Stack
            sx={{
                flex: '1',
                display: 'flex',
                flexDirection: 'column'
            }}
            onContextMenu={handleContextMenu}
        >
            <ConfigureParametersChartDialog
                isOpen={isConfigureChartDialogOpen}
                setIsOpen={setIsConfigureChartDialogOpen}
            />
            <Menu
                open={contextMenu !== null}
                onClose={handleClose}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                }
            >
                <MenuItem onClick={handleOpeningConfigureChartsDialog}>Configure chart...</MenuItem>
                <MenuItem onClick={handleChangeChartTooltipVisibility}>
                    {
                        isChartTooltipVisible
                            ? "Hide tooltip"
                            : "Show tooltip"
                    }
                </MenuItem>
            </Menu>
            <ReactEcharts
                theme="light"
                style={{
                    flex: '1'
                }}
                option={{
                    grid: {
                        left: 50,
                        right: 10,
                        top: 20,
                        bottom: 20,
                    },
                    legend: {
                        show: false,
                            selected: {
                                'Current balance': true,
                                'Expiration balance': true,
                            },
                        top: 5,
                    },
                    tooltip: {
                        trigger: 'axis',
                        showContent: isChartTooltipVisible,
                        position: chartTooltipService.getTooltipPositionForBalanceProfileChart(
                            selectedPortfolio!.portfolioSettings!, false),
                    },
                    xAxis: {
                        type: 'category',
                    },
                    yAxis: [
                        {
                            gridIndex: 0,
                            position: 'left',
                            axisTick: {
                                show: true,
                                lineStyle: {
                                    color: 'rgb(200,200,200)'
                                },
                            },
                            splitLine: {
                                show: true,
                                lineStyle: {
                                    color: 'rgb(200,200,200)'
                                },
                            },
                            minorSplitLine: {
                                show: true,
                                splitNumber: 5,
                                lineStyle: {
                                    color: 'rgb(205,205,205)',
                                    type: 'dotted'
                                }
                            }
                        }
                    ],
                    dataZoom: [
                        {
                            show: true,
                            type: 'inside',
                            filterMode: 'none',
                            xAxisIndex: [0],
                            startValue: 'min',
                            endValue: 'max'
                        },
                        {
                            show: true,
                            type: 'inside',
                            filterMode: 'none',
                            yAxisIndex: [0],
                            startValue: 'min',
                            endValue: 'max'
                        }
                    ],
                    dataset: {
                        source: dataSource,
                    },
                    series: [
                        {
                            animation: false,
                            name: 'Current balance',
                            type: 'line',
                            smooth: false,
                            color: 'black',
                            seriesLayoutBy: 'row',
                            emphasis: { focus: 'series' },
                            lineStyle: {
                                type: 'solid',
                                width: 2,
                            },
                            showSymbol: false,
                            yAxisIndex: 0,
                            tooltip: {
                                valueFormatter: (value: number) => value?.toFixed(2) ?? 'Not available'
                            }
                        },
                        {
                            animation: false,
                            name: 'Expiration balance',
                            type: 'line',
                            smooth: false,
                            color: 'green',
                            seriesLayoutBy: 'row',
                            emphasis: { focus: 'series' },
                            lineStyle: {
                                type: 'solid',
                                width: 2,
                            },
                            showSymbol: false,
                            yAxisIndex: 0,
                            tooltip: {
                                valueFormatter: (value: number) => value?.toFixed(2) ?? 'Not available'
                            }
                        },
                    ]
                }}
            />
        </Stack>
    );
}

function getDataSource(
    portfolioBalanceHistory: PortfolioBalanceHistoryEntryModel[],
    settings: PortfolioSettingsModel
): any[][] {
    const result: any[][] = [];
    
    result.push(portfolioBalanceHistory.map(x => x.createdAt));
    result.push(portfolioBalanceHistory
        .map(settings.hideCommitedBalance
            ? x => x.currentBalance - settings.commitedBalance
            : x => x.currentBalance));
    result.push(portfolioBalanceHistory
        .map(settings.hideCommitedBalance
            ? x => x.expirationBalance - settings.commitedBalance
            : x => x.expirationBalance));
    
    return result;
}